import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/authService';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import ChangeEmailModal from '../../components/AccountModals/ChangeEmailModal';
import ChangePhoneModal from '../../components/AccountModals/ChangePhoneModal';
import ChangePasswordModal from '../../components/AccountModals/ChangePasswordModal';
import SubNav from '../../components/SubNav/SubNav';
import { homeService } from '../../services/homeService';
import './Account.css';
import '../../styles/Dashboard.css';
import { useNavigate } from 'react-router-dom';
import AdminPanel from '../../components/AdminPanel/AdminPanel';

const NOINDEX_HELMET = (
  <Helmet>
    <meta name="robots" content="noindex, nofollow" />
    <title>Account | NowTarot AI</title>
  </Helmet>
);

const Account = () => {
  const { user, loading } = useAuth();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userData, setUserData] = useState(null);
  const [activeModal, setActiveModal] = useState(null);
  const [savingPreference, setSavingPreference] = useState(false);
  const [clearingHistory, setClearingHistory] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const data = await authService.getCurrentUser();
        setUserData(data);
      } catch (err) {
        console.error('Failed to fetch user data:', err);
        navigate('/');
      }
    };

    if (!loading && user) {
      fetchUserData();
    }
  }, [loading, user, navigate]);

  if (loading || !userData) {
    return (
      <div className="dashboard-page">
        <Header />
        <SubNav />
        <div className="dashboard-container">
          <div className="loading-spinner">Loading...</div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!user) {
    navigate('/');
    return null;
  }

  const handleUpdateEmail = async (formData) => {
    try {
      await authService.updateEmail(formData.newEmail, formData.password);
      setSuccess('Email updated successfully');
      setError('');
      const data = await authService.getCurrentUser();
      setUserData(data);
    } catch (err) {
      throw err;
    }
  };

  const handleUpdatePhone = async (formData) => {
    try {
      await authService.updatePhone(formData.newPhone, formData.password, formData.verificationCode);
      setSuccess('Phone number updated successfully');
      setError('');
      const data = await authService.getCurrentUser();
      setUserData(data);
    } catch (err) {
      throw err;
    }
  };

  const handleChangePassword = async (formData) => {
    try {
      await authService.changePassword(formData.currentPassword, formData.newPassword);
      setSuccess('Password changed successfully');
      setError('');
    } catch (err) {
      throw err;
    }
  };

  const handleToggleSaveQuestions = async () => {
    const nextValue = !(userData?.save_questions !== false);
    setSavingPreference(true);
    setError('');
    try {
      const result = await authService.updateSaveQuestions(nextValue);
      setUserData((prev) => ({ ...prev, save_questions: result.save_questions }));
      setSuccess(
        result.save_questions
          ? 'Future questions will be saved to your history.'
          : 'Future questions will not be saved to your history.'
      );
    } catch (err) {
      setError(err.message || 'Could not update question-saving preference.');
    } finally {
      setSavingPreference(false);
    }
  };

  const handleClearQuestionHistory = async () => {
    const confirmed = window.confirm(
      'Delete all of your saved questions and answers? Any reviews tied to those questions will also be removed. This cannot be undone.'
    );
    if (!confirmed) {
      return;
    }

    setClearingHistory(true);
    setError('');
    try {
      const result = await homeService.clearReadingHistory();
      setSuccess(
        `Cleared ${result.deleted_readings || 0} saved question${result.deleted_readings === 1 ? '' : 's'}` +
          (result.deleted_reviews
            ? ` and ${result.deleted_reviews} linked review${result.deleted_reviews === 1 ? '' : 's'}.`
            : '.')
      );
    } catch (err) {
      setError(err.message || 'Could not clear your saved questions.');
    } finally {
      setClearingHistory(false);
    }
  };

  const saveQuestionsEnabled = userData?.save_questions !== false;

  return (
    <div className="dashboard-page">
      {NOINDEX_HELMET}
      <Header />
      <SubNav />
      
      <div className="dashboard-container">
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {/* Account Info Card */}
        <div className="dash-card">
          <div className="dash-card-header">
            <h3 className="dash-card-title">
              <span className="card-icon">👤</span>
              Account Information
            </h3>
          </div>
          
          <div className="info-list">
            <div className="info-list-item">
              <div>
                <div className="info-list-label">Name</div>
                <div className="info-list-value">{userData?.name}</div>
              </div>
            </div>
            
            <div className="info-list-item">
              <div>
                <div className="info-list-label">Email</div>
                <div className="info-list-value">{userData?.email}</div>
              </div>
              <button 
                className="info-list-action"
                onClick={() => setActiveModal('email')}
              >
                Change
              </button>
            </div>
            
            {userData?.calling_enabled && (
              <div className="info-list-item">
                <div>
                  <div className="info-list-label">Phone Number</div>
                  <div className="info-list-value">{userData?.phone_number}</div>
                </div>
                <button
                  className="info-list-action"
                  onClick={() => setActiveModal('phone')}
                >
                  Change
                </button>
              </div>
            )}
            
            <div className="info-list-item">
              <div>
                <div className="info-list-label">Password</div>
                <div className="info-list-value">••••••••</div>
              </div>
              <button 
                className="info-list-action"
                onClick={() => setActiveModal('password')}
              >
                Change
              </button>
            </div>

            {userData?.created_at && (
              <div className="info-list-item">
                <div>
                  <div className="info-list-label">Member Since</div>
                  <div className="info-list-value">
                    {new Date(userData.created_at).toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Privacy / Questions Card */}
        <div className="dash-card">
          <div className="dash-card-header">
            <h3 className="dash-card-title">
              <span className="card-icon">🔒</span>
              Question Privacy
            </h3>
          </div>

          <div className="privacy-settings">
            <div className="privacy-setting-row">
              <div>
                <div className="info-list-label">Save my questions</div>
                <div className="privacy-setting-copy">
                  When on, completed questions appear in your Home history so you can re-read them.
                  Turn this off if you do not want future questions saved.
                </div>
              </div>
              <button
                type="button"
                className={`privacy-toggle ${saveQuestionsEnabled ? 'on' : 'off'}`}
                onClick={handleToggleSaveQuestions}
                disabled={savingPreference}
                aria-pressed={saveQuestionsEnabled}
              >
                {savingPreference ? 'Saving…' : saveQuestionsEnabled ? 'On' : 'Off'}
              </button>
            </div>

            <div className="privacy-setting-row">
              <div>
                <div className="info-list-label">Clear saved questions</div>
                <div className="privacy-setting-copy">
                  Permanently deletes your saved questions and answers. Reviews linked to those
                  questions are removed too.
                </div>
              </div>
              <button
                type="button"
                className="review-btn privacy-clear-btn"
                onClick={handleClearQuestionHistory}
                disabled={clearingHistory}
              >
                {clearingHistory ? 'Clearing…' : 'Clear questions'}
              </button>
            </div>
          </div>
        </div>

        {/* Account Status Card */}
        <div className="dash-card">
          <div className="dash-card-header">
            <h3 className="dash-card-title">
              <span className="card-icon">✓</span>
              Account Status
            </h3>
          </div>
          
          <div className="status-grid">
            {userData?.calling_enabled && (
              <div className="status-item">
                <div className={`status-icon ${userData?.phone_number ? 'verified' : 'pending'}`}>
                  {userData?.phone_number ? '✓' : '!'}
                </div>
                <div className="status-text">
                  <div className="status-label">Phone Verified</div>
                  <div className="status-value">
                    {userData?.phone_number
                      ? 'Your phone number is verified'
                      : 'No phone number on file'}
                  </div>
                </div>
              </div>
            )}

            <div className="status-item">
              <div className={`status-icon ${userData?.is_email_confirmed ? 'verified' : 'pending'}`}>
                {userData?.is_email_confirmed ? '✓' : '!'}
              </div>
              <div className="status-text">
                <div className="status-label">Email Confirmed</div>
                <div className="status-value">
                  {userData?.is_email_confirmed
                    ? 'Your email address is confirmed'
                    : 'Check your inbox to confirm your email'}
                </div>
              </div>
            </div>

            <div className="status-item">
              <div className={`status-icon ${userData?.is_deactivated ? 'pending' : 'verified'}`}>
                {userData?.is_deactivated ? '!' : '✓'}
              </div>
              <div className="status-text">
                <div className="status-label">Account Active</div>
                <div className="status-value">
                  {userData?.is_deactivated
                    ? 'Your account is deactivated'
                    : 'Your account is in good standing'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modals */}
        <ChangeEmailModal
          isOpen={activeModal === 'email'}
          onClose={() => setActiveModal(null)}
          onSubmit={handleUpdateEmail}
        />
        
        <ChangePhoneModal
          isOpen={activeModal === 'phone'}
          onClose={() => setActiveModal(null)}
          onSubmit={handleUpdatePhone}
        />
        
        <ChangePasswordModal
          isOpen={activeModal === 'password'}
          onClose={() => setActiveModal(null)}
          onSubmit={handleChangePassword}
        />
      </div>

      {userData?.id === 1 && <AdminPanel />}
      <Footer />
    </div>
  );
};

export default Account;
